// src/pages/AboutUs.js - Redesigned to match main design
import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Globe, Brush, Rocket, Quote, Users, Trophy, Star, Calendar, ChevronRight } from "lucide-react";
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

/* ═══════════════════════════════════════════════════════════════════
   GLOBAL STYLES - Matching Home.js design system
══════════════════════════════════════════════════════════════════ */
const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@700;900&display=swap');
    @font-face { font-family:'Azonix'; src:url('/fonts/Azonix.otf') format('opentype'),url('/fonts/azonix.woff2') format('woff2'); font-display:swap; }
    @font-face { font-family:'Japanese3017'; src:url('/fonts/Japanese-3017.ttf') format('truetype'); font-display:swap; }
    @font-face { font-family:'Acknowledgement'; src:url('/fonts/Acknowledgement.otf') format('opentype'); font-display:swap; }

    :root {
      --az: 'Azonix','Impact',sans-serif;
      --jp: 'Japanese3017','Noto Serif JP',serif;
      --wt: 'Acknowledgement','Azonix',sans-serif;
      --ui: -apple-system,'Helvetica Neue',sans-serif;
      --bd: 'Georgia',serif;
      --neu: #a78bfa;
      --bg: #08080c;
      --glass: rgba(255,255,255,0.04);
      --gb: rgba(255,255,255,0.08);
    }

    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}

    .about-root {
      background: var(--bg);
      color: #fff;
      font-family: var(--ui);
      overflow-x: hidden;
      min-height: 100vh;
      position: relative;
    }

    .about-wrap {
      max-width: 1240px;
      margin: 0 auto;
      padding: 0 clamp(16px,4vw,52px);
      position: relative;
      z-index: 1;
    }

    .about-sec { padding: clamp(56px,8vh,100px) 0; }
    .about-sec-sm { padding: clamp(36px,5vh,64px) 0; }

    /* Section Header */
    .about-sh {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 28px;
      gap: 16px;
    }
    .about-sh-left { display:flex; align-items:center; gap:12px; }
    .about-sh-bar { width:3px; height:26px; border-radius:2px; flex-shrink:0; }
    .about-sh-label {
      font-family: var(--az);
      font-size: 16px;
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Glass Card */
    .about-card {
      background: var(--glass);
      backdrop-filter: blur(24px);
      -webkit-backdrop-filter: blur(24px);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: clamp(20px,3vw,32px);
      transition: all .3s ease;
    }
    .about-card:hover {
      background: rgba(255,255,255,0.07);
      border-color: rgba(255,255,255,0.14);
      transform: translateY(-4px);
    }

    /* Stats Grid */
    .about-stats-grid {
      display: grid;
      grid-template-columns: repeat(4,1fr);
      gap: 14px;
    }
    @media(max-width:768px){ .about-stats-grid { grid-template-columns: repeat(2,1fr); } }

    .about-stat-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 14px;
      padding: clamp(16px,2vw,24px);
      text-align: center;
      transition: all .3s ease;
    }
    .about-stat-card:hover {
      transform: translateY(-4px);
      border-color: rgba(167,139,250,0.3);
      box-shadow: 0 12px 40px rgba(167,139,250,0.1);
    }
    .about-stat-icon {
      width: 44px; height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, rgba(167,139,250,0.15), rgba(167,139,250,0.05));
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 12px;
    }
    .about-stat-val {
      font-family: var(--az);
      font-size: clamp(22px,3vw,32px);
      color: #fff;
      margin-bottom: 4px;
    }
    .about-stat-lbl {
      font-size: 11px;
      color: rgba(255,255,255,0.38);
      letter-spacing: 1px;
      text-transform: uppercase;
    }

    /* Mission/Vision Cards */
    .about-mv-grid {
      display: grid;
      grid-template-columns: repeat(2,1fr);
      gap: 20px;
    }
    @media(max-width:768px){ .about-mv-grid { grid-template-columns: 1fr; } }

    .about-mv-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 18px;
      padding: clamp(24px,3vw,36px);
      position: relative;
      overflow: hidden;
    }
    .about-mv-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(to right, transparent, var(--neu), transparent);
    }
    .about-mv-icon {
      width: 48px; height: 48px;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .about-mv-title {
      font-family: var(--az);
      font-size: clamp(18px,2.5vw,24px);
      color: #fff;
      margin-bottom: 12px;
    }
    .about-mv-text {
      font-size: 14px;
      color: rgba(255,255,255,0.42);
      line-height: 1.8;
      font-family: var(--bd);
    }

    /* Values Grid */
    .about-values-grid {
      display: grid;
      grid-template-columns: repeat(3,1fr);
      gap: 16px;
    }
    @media(max-width:900px){ .about-values-grid { grid-template-columns: repeat(2,1fr); } }
    @media(max-width:600px){ .about-values-grid { grid-template-columns: 1fr; } }

    .about-value-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 16px;
      padding: 24px;
      transition: all .35s ease;
    }
    .about-value-card:hover {
      transform: translateY(-6px);
      border-color: rgba(167,139,250,0.25);
      box-shadow: 0 20px 60px rgba(0,0,0,0.3);
    }
    .about-value-icon {
      width: 52px; height: 52px;
      border-radius: 14px;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 16px;
    }
    .about-value-title {
      font-family: var(--az);
      font-size: 16px;
      color: #fff;
      margin-bottom: 8px;
    }
    .about-value-desc {
      font-size: 13px;
      color: rgba(255,255,255,0.38);
      line-height: 1.6;
    }

    /* Milestones */
    .about-milestones-grid {
      display: grid;
      grid-template-columns: repeat(3,1fr);
      gap: 14px;
    }
    @media(max-width:900px){ .about-milestones-grid { grid-template-columns: repeat(2,1fr); } }
    @media(max-width:600px){ .about-milestones-grid { grid-template-columns: 1fr; } }

    .about-milestone-card {
      background: var(--glass);
      border: 1px solid var(--gb);
      border-radius: 14px;
      padding: 20px;
      transition: all .3s ease;
    }
    .about-milestone-card:hover {
      border-color: rgba(167,139,250,0.2);
      transform: translateY(-3px);
    }
    .about-milestone-year {
      font-family: var(--az);
      font-size: 12px;
      color: var(--neu);
      margin-bottom: 8px;
      display: flex; align-items: center; gap: 6px;
    }
    .about-milestone-title {
      font-size: 15px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 4px;
    }
    .about-milestone-desc {
      font-size: 12px;
      color: rgba(255,255,255,0.38);
    }

    /* Team Grid */
    .about-team-grid {
      display: grid;
      grid-template-columns: repeat(4,1fr);
      gap: 16px;
    }
    @media(max-width:1024px){ .about-team-grid { grid-template-columns: repeat(2,1fr); } }
    @media(max-width:520px){ .about-team-grid { grid-template-columns: 1fr; } }

    .about-team-card {
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      aspect-ratio: 3/4;
    }
    .about-team-img {
      position: absolute;
      inset: 0;
      background-size: cover;
      background-position: center;
      transition: transform .6s ease;
    }
    .about-team-card:hover .about-team-img { transform: scale(1.08); }
    .about-team-grad {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.4) 50%, transparent 100%);
    }
    .about-team-body {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 20px;
    }
    .about-team-name {
      font-family: var(--az);
      font-size: 15px;
      color: #fff;
      margin-bottom: 2px;
    }
    .about-team-role {
      font-size: 11px;
      color: var(--neu);
      letter-spacing: 1px;
      text-transform: uppercase;
      margin-bottom: 8px;
    }
    .about-team-quote {
      font-size: 11px;
      color: rgba(255,255,255,0.5);
      font-style: italic;
      line-height: 1.5;
    }

    /* Quote Section */
    .about-quote {
      text-align: center;
      padding: clamp(40px,6vw,64px);
      background: linear-gradient(135deg, rgba(167,139,250,0.06) 0%, rgba(236,72,153,0.04) 100%);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 22px;
      position: relative;
    }
    .about-quote-glow {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%,-50%);
      width: 400px; height: 160px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.08), transparent);
      filter: blur(40px);
      pointer-events: none;
    }
    .about-quote-icon {
      position: relative;
      z-index: 1;
      margin-bottom: 16px;
    }
    .about-quote-text {
      position: relative;
      z-index: 1;
      font-size: clamp(16px,2vw,20px);
      font-style: italic;
      color: rgba(255,255,255,0.8);
      font-family: var(--bd);
      line-height: 1.7;
      max-width: 640px;
      margin: 0 auto 16px;
    }
    .about-quote-author {
      position: relative;
      z-index: 1;
      font-family: var(--az);
      font-size: 12px;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: var(--neu);
    }

    /* CTA Section */
    .about-cta {
      max-width: 680px;
      margin: 0 auto;
      border-radius: 22px;
      text-align: center;
      background: var(--glass);
      border: 1px solid var(--gb);
      backdrop-filter: blur(24px);
      padding: clamp(32px,5vw,56px) clamp(22px,4vw,40px);
      position: relative;
      overflow: hidden;
    }
    .about-cta-glow {
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%,-50%);
      width: 320px; height: 140px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.1), transparent);
      filter: blur(40px);
      pointer-events: none;
    }
    .about-cta-title {
      position: relative;
      z-index: 1;
      font-family: var(--az);
      font-size: clamp(18px,3vw,26px);
      color: #fff;
      margin-bottom: 12px;
    }
    .about-cta-desc {
      position: relative;
      z-index: 1;
      font-size: 14px;
      color: rgba(255,255,255,0.42);
      line-height: 1.8;
      font-family: var(--bd);
      max-width: 440px;
      margin: 0 auto 24px;
    }
    .about-cta-btns {
      position: relative;
      z-index: 1;
      display: flex;
      gap: 12px;
      justify-content: center;
      flex-wrap: wrap;
    }
    .about-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      text-decoration: none;
      font-family: var(--az);
      font-size: 10px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      transition: all .25s;
      white-space: nowrap;
    }
    .about-btn-primary {
      background: linear-gradient(135deg, var(--neu), #8b5cf6);
      color: #fff;
      box-shadow: 0 8px 24px rgba(167,139,250,0.3);
    }
    .about-btn-primary:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 36px rgba(167,139,250,0.5);
    }
    .about-btn-ghost {
      border: 1px solid rgba(167,139,250,0.32);
      color: var(--neu);
    }
    .about-btn-ghost:hover {
      background: rgba(167,139,250,0.1);
      border-color: rgba(167,139,250,0.5);
    }

    /* Hero */
    .about-hero {
      position: relative;
      min-height: 50vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }
    .about-hero-bg {
      position: absolute;
      inset: 0;
      background: linear-gradient(135deg, rgba(167,139,250,0.08) 0%, rgba(236,72,153,0.04) 50%, rgba(99,102,241,0.06) 100%);
    }
    .about-hero-glow1 {
      position: absolute;
      top: 20%; left: 15%;
      width: 300px; height: 300px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(167,139,250,0.12), transparent);
      filter: blur(60px);
    }
    .about-hero-glow2 {
      position: absolute;
      bottom: 20%; right: 15%;
      width: 250px; height: 250px;
      border-radius: 50%;
      background: radial-gradient(ellipse, rgba(236,72,153,0.1), transparent);
      filter: blur(50px);
    }
    .about-hero-content {
      position: relative;
      z-index: 1;
      text-align: center;
      padding: 80px 20px 60px;
    }
    .about-hero-title {
      font-family: var(--az);
      font-size: clamp(36px,6vw,64px);
      letter-spacing: -1px;
      margin-bottom: 16px;
    }
    .about-hero-title span {
      background: linear-gradient(135deg, var(--neu), #ec4899);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .about-hero-desc {
      font-size: clamp(14px,1.5vw,17px);
      color: rgba(255,255,255,0.42);
      max-width: 500px;
      margin: 0 auto;
      font-family: var(--bd);
      line-height: 1.8;
    }

    /* Divider */
    .about-div {
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(255,255,255,0.06), transparent);
      margin: 0 0 clamp(48px,7vh,80px);
    }

    /* Story Section */
    .about-story {
      max-width: 720px;
      margin: 0 auto;
      text-align: center;
    }
    .about-story-title {
      font-family: var(--az);
      font-size: clamp(24px,4vw,36px);
      margin-bottom: 20px;
    }
    .about-story-title span { color: #ec4899; }
    .about-story-text {
      font-size: 14px;
      color: rgba(255,255,255,0.42);
      line-height: 2;
      font-family: var(--bd);
    }
    .about-story-text p { margin-bottom: 16px; }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   PARTICLE BACKGROUND
══════════════════════════════════════════════════════════════════ */
const ParticleBackground = () => {
  const ref = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;

    const resize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('resize', resize);

    const pts = Array.from({ length: 40 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - .5) * .2, vy: (Math.random() - .5) * .2,
      r: Math.random() * 1.2 + .3, a: Math.random() * .35 + .07,
      h: [250, 270, 280][Math.floor(Math.random() * 3)],
    }));

    const mm = (e) => { mouse.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('mousemove', mm);

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const { x: mx, y: my } = mouse.current;
      for (let i = 0; i < pts.length; i++)
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < 100) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(167,139,250,${.05 * (1 - d / 100)})`; ctx.lineWidth = .4; ctx.stroke(); }
        }
      pts.forEach(p => {
        const d = Math.hypot(mx - p.x, my - p.y);
        if (d < 90) { p.vx -= (mx - p.x) / d * .028; p.vy -= (my - p.y) / d * .028; }
        p.vx *= .99; p.vy *= .99; p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},72%,70%,${p.a})`; ctx.fill();
      });
      const g = ctx.createRadialGradient(mx, my, 0, mx, my, 160);
      g.addColorStop(0, 'rgba(167,139,250,0.04)'); g.addColorStop(1, 'transparent');
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      raf.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener('resize', resize); window.removeEventListener('mousemove', mm); cancelAnimationFrame(raf.current); };
  }, []);

  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: .5 }} />;
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
export default function AboutUs() {
  const values = [
    { 
      icon: Globe, 
      title: "Cultural Unity", 
      desc: "Bridging Eastern and Western storytelling traditions into one unified creative universe.",
      color: '#3b82f6'
    },
    { 
      icon: Brush, 
      title: "Creativity", 
      desc: "Unlimited imagination powering the future of animation and digital storytelling.",
      color: '#ec4899'
    },
    { 
      icon: Rocket, 
      title: "Innovation", 
      desc: "Building immersive metaverse-powered experiences that redefine entertainment.",
      color: '#8b5cf6'
    },
  ];

  const team = [
    { 
      name: "Arinze Xylo", 
      role: "Operative Director", 
      image: "/assets/team1.jpg", 
      quote: "Innovation drives the future and we are that future."
    },
    { 
      name: "Augustine Eleon", 
      role: "Lead Developer", 
      image: "/assets/team2.jpg", 
      quote: "Code is the bridge between imagination and reality."
    },
    { 
      name: "Jesse Rumour", 
      role: "Community Director", 
      image: "/assets/batman-rumour.jpg", 
      quote: "A community of minds is one that cannot perish."
    },
    { 
      name: "Rick Toya", 
      role: "Design Manager", 
      image: "/assets/team3.jpg", 
      quote: "Without design, there is no creation and without creation, there is no life."
    },
  ];

  const milestones = [
    { year: "2024", title: "ANIMAC Founded", desc: "Started as a dream to unite anime cultures worldwide" },
    { year: "2024", title: "Outrange Launch", desc: "Launched our entertainment news platform" },
    { year: "2025", title: "10K Community", desc: "Reached 10,000 active community members" },
    { year: "2025", title: "Streamfinite Beta", desc: "Released streaming guides preview" },
    { year: "2026", title: "Global Expansion", desc: "Expanded to serve fans in 50+ countries" },
    { year: "2026", title: "Creator Program", desc: "Launched featured creators initiative" },
  ];

  const stats = [
    { value: "50+", label: "Countries", icon: Globe },
    { value: "15K+", label: "Community Members", icon: Users },
    { value: "500+", label: "Articles Published", icon: Star },
    { value: "50+", label: "Featured Creators", icon: Trophy },
  ];

  return (
    <>
      <PageStyles />
      <SEO
        title="About ANIMAC - Shaping the Metaverse Through Animation & Culture"
        description="Learn about ANIMAC's mission to merge cultural worlds through animation. From Lagos to Tokyo, we're building a global community of creators and innovators."
        url="/about"
        image="/assets/animac-preview-logo.svg"
        type="website"
      />

      <ParticleBackground />

      <div className="about-root">
        {/* Hero */}
        <div className="about-hero">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=1920&q=80" 
              alt="About ANIMAC" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/75 to-gray-900/85" />
          </div>
          <div className="about-hero-bg" />
          <div className="about-hero-glow1" />
          <div className="about-hero-glow2" />
          <div className="about-hero-content">
            <motion.h1
              className="about-hero-title"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              About <span>ANIMAC</span>
            </motion.h1>
            <motion.p
              className="about-hero-desc"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Where culture meets creativity — shaping the metaverse through imagination, innovation, and storytelling.
            </motion.p>
          </div>
        </div>

        <div className="about-wrap">
          {/* Stats */}
          <section className="about-sec-sm" style={{ marginTop: '-32px', position: 'relative', zIndex: 2 }}>
            <div className="about-stats-grid">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="about-stat-card"
                >
                  <div className="about-stat-icon">
                    <stat.icon size={20} color="#a78bfa" />
                  </div>
                  <div className="about-stat-val">{stat.value}</div>
                  <div className="about-stat-lbl">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Mission & Vision */}
          <section className="about-sec">
            <div className="about-mv-grid">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="about-mv-card"
              >
                <div className="about-mv-icon" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(139,92,246,0.08))' }}>
                  <Rocket size={24} color="#8b5cf6" />
                </div>
                <h2 className="about-mv-title">Our Mission</h2>
                <p className="about-mv-text">
                  To create a unified global community that celebrates both Eastern and Western animation culture. 
                  We strive to make entertainment accessible to everyone, bridging cultural divides through 
                  compelling storytelling, innovative technology, and passionate community engagement.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="about-mv-card"
              >
                <div className="about-mv-icon" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(236,72,153,0.08))' }}>
                  <Star size={24} color="#ec4899" />
                </div>
                <h2 className="about-mv-title">Our Vision</h2>
                <p className="about-mv-text">
                  To become the leading global platform where anime, cartoons, and entertainment converge. 
                  We envision a world where animation transcends borders, connecting audiences worldwide 
                  through immersive digital experiences and cultural fusion.
                </p>
              </motion.div>
            </div>
          </section>

          <div className="about-div" />

          {/* Values */}
          <section className="about-sec">
            <div className="about-sh">
              <div className="about-sh-left">
                <div className="about-sh-bar" style={{ background: 'linear-gradient(to bottom, #a78bfa, transparent)' }} />
                <span className="about-sh-label" style={{ color: '#a78bfa' }}>Our Values</span>
              </div>
            </div>
            
            <div className="about-values-grid">
              {values.map((val, i) => (
                <motion.div
                  key={i}
                  className="about-value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="about-value-icon" style={{ background: `linear-gradient(135deg, ${val.color}22, ${val.color}08)` }}>
                    <val.icon size={24} color={val.color} />
                  </div>
                  <h3 className="about-value-title">{val.title}</h3>
                  <p className="about-value-desc">{val.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Story */}
          <section className="about-sec">
            <div className="about-story">
              <h2 className="about-story-title">
                Our <span>Story</span>
              </h2>
              <div className="about-story-text">
                <p>
                  Born from a passion to merge cultural worlds, ANIMAC redefines how we experience animation. 
                  From Lagos to Tokyo, from Hollywood to Seoul, we're building a global community of dreamers, 
                  creators, and innovators pushing the limits of imagination.
                </p>
                <p>
                  What started as a small group of anime enthusiasts has grown into a thriving platform 
                  connecting fans across continents. We believe that great stories have no boundaries, 
                  and our mission is to bring these stories to every corner of the world.
                </p>
              </div>
            </div>
          </section>

          <div className="about-div" />

          {/* Milestones */}
          <section className="about-sec">
            <div className="about-sh">
              <div className="about-sh-left">
                <div className="about-sh-bar" style={{ background: 'linear-gradient(to bottom, #ec4899, transparent)' }} />
                <span className="about-sh-label" style={{ color: '#ec4899' }}>Our Journey</span>
              </div>
            </div>
            
            <div className="about-milestones-grid">
              {milestones.map((milestone, i) => (
                <motion.div
                  key={i}
                  className="about-milestone-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="about-milestone-year">
                    <Calendar size={12} />{milestone.year}
                  </div>
                  <h3 className="about-milestone-title">{milestone.title}</h3>
                  <p className="about-milestone-desc">{milestone.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          <div className="about-div" />

          {/* Team */}
          <section className="about-sec">
            <div className="about-sh">
              <div className="about-sh-left">
                <div className="about-sh-bar" style={{ background: 'linear-gradient(to bottom, #8b5cf6, transparent)' }} />
                <span className="about-sh-label" style={{ color: '#8b5cf6' }}>Meet the Team</span>
              </div>
            </div>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.38)', textAlign: 'center', marginBottom: '28px', fontFamily: 'var(--bd)' }}>
              The passionate people behind ANIMAC who work tirelessly to bring entertainment to the world.
            </p>
            
            <div className="about-team-grid">
              {team.map((member, i) => (
                <motion.div
                  key={i}
                  className="about-team-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="about-team-img" style={{ backgroundImage: `url(${member.image})` }} />
                  <div className="about-team-grad" />
                  <div className="about-team-body">
                    <h3 className="about-team-name">{member.name}</h3>
                    <p className="about-team-role">{member.role}</p>
                    <p className="about-team-quote">"{member.quote}"</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Quote */}
          <section className="about-sec-sm">
            <div className="about-quote">
              <div className="about-quote-glow" />
              <Quote className="about-quote-icon" size={36} color="#a78bfa" />
              <p className="about-quote-text">
                "Animation is the language of the soul, transcending borders and building bridges between worlds."
              </p>
              <p className="about-quote-author">— ANIMAC Philosophy</p>
            </div>
          </section>

          {/* CTA */}
          <section className="about-sec">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="about-cta"
            >
              <div className="about-cta-glow" />
              <h2 className="about-cta-title">Join the ANIMAC Community</h2>
              <p className="about-cta-desc">
                Be part of something bigger. Connect with fellow anime enthusiasts, stay updated on the latest news, 
                and help shape the future of entertainment!
              </p>
              <div className="about-cta-btns">
                <Link to="/outrange" className="about-btn about-btn-primary">
                  Explore Content <ChevronRight size={14} />
                </Link>
                <Link to="/contact" className="about-btn about-btn-ghost">
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </>
  );
}
